import { IdentityAvatar } from '@/heroui/IdentityAvatar';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/lib/toast';
import { TeamTag } from '@/components/TeamTag';
import { api } from '@/lib/api';
import { errToToast } from '@/lib/humanizeApiError';
import { useAuthStore } from '@/stores/authStore';
import { RangeSlider } from '@/components/ui/RangeSlider';
import { Button } from '@/components/Button';
import { useDataStore } from '@/stores/dataStore';
import { useBlockedUsers } from '@/lib/useBlockedUsers';
import { openReportModal } from '@/components/ReportModal';
import { useFriends } from '@/lib/useFriends';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useGuildRoster, guildRosterKey } from '@/lib/useGuildRoster';
import { useGuildBans } from '@/lib/useGuildBans';
import { useMyGuildPermissions, canManageMember, canIn, PERMISSIONS } from '@/lib/guildPermissions';
import { buildPublicInviteUrl } from '@/lib/inviteLinks';
import { clampRoleColor, resolveRoleColor } from '@/lib/roleColor';
import { useTheme } from '@/hooks/useTheme';
import { presenceLabel, presenceDotClass, resolveDisplayedStatus } from '@/lib/presence';
import { setStatusFor } from '@/lib/statusTimer';
import { ActionRow } from '@/components/userCard/ActionRow';
import { ProfileSidePane } from '@/components/userCard/ProfileSidePane';
import { UserCardProfileSections } from '@/components/userCard/UserCardProfileSections';
import {
  AlarmClockIcon,
  ChatBubbleIcon,
  ChevronDownIcon,
  CloseIcon,
  CopyIcon,
  CrownIcon,
  LogOutIcon,
  PlusIcon,
  SettingsIcon,
  ShieldBanIcon,
  ShieldIcon,
  FlagIcon,
  UsersIcon,
  VolumeIcon,
  VolumeOffIcon,
} from '@/components/icons';
import {
  getUserVolume,
  isLocallyMuted,
  setUserVolume,
  toggleLocalMute,
  useVoiceLocalAudio,
} from '@/lib/voiceLocalAudio';
import type { GuildRole, UserStatus } from '@/types/api';
import { isPlatformEmbedded, openPlatformView } from '@/lib/platformEmbed';
import { useShellHostsPresence } from '@/lib/shellPresence';

/** Own-profile status options (set via api.updateMe). */
const STATUS_OPTIONS: { value: UserStatus; labelKey: string }[] = [
  { value: 'ONLINE', labelKey: 'prof.card.statusOnline' },
  { value: 'IDLE', labelKey: 'prof.card.statusIdle' },
  { value: 'DND', labelKey: 'prof.card.statusDnd' },
  { value: 'INVISIBLE', labelKey: 'prof.card.statusInvisible' },
];

/** "Set status for…" durations (Discord-style). null = until changed. */
const STATUS_DURATIONS: { labelKey: string; ms: number | null }[] = [
  { labelKey: 'prof.card.duration15m', ms: 15 * 60_000 },
  { labelKey: 'prof.card.duration1h', ms: 60 * 60_000 },
  { labelKey: 'prof.card.duration8h', ms: 8 * 60 * 60_000 },
  { labelKey: 'prof.card.duration24h', ms: 24 * 60 * 60_000 },
  { labelKey: 'prof.card.duration3d', ms: 3 * 24 * 60 * 60_000 },
  { labelKey: 'prof.card.durationForever', ms: null },
];

/** Minimal user shape any trigger can hand the card (message author, member row…). */
export interface UserCardUser {
  id: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  status?: UserStatus;
  statusText?: string | null;
  bio?: string | null;
  teamTag?: string | null;
  teamName?: string | null;
}

/**
 * Reusable user hovercard. Opened from the member list and from message
 * authors. In a guild context it resolves the member's roles (chips) and
 * standing via the cached roster; in DM context it shows the basic profile.
 *
 * Actions: open DM, block/unblock (synced with Privacy settings via the
 * shared blocks query), copy id / @username, and the friends control —
 * add / accept / decline / cancel / unfriend, driven by useFriends and the
 * relationship returned by stateOf().
 */
export function UserCard(props: {
  user: UserCardUser;
  guildId?: string | null;
  onClose: () => void;
  /** 'dialog' renders the wider "full profile" layout (no nested expand row). */
  variant?: 'popover' | 'dialog';
  /** When provided (popover variant), shows a "Полный профиль" row that the
   *  host opens as a centred modal — kept outside the popover so an
   *  outside-click on the modal doesn't unmount it. */
  onOpenFull?: () => void;
  /** When opened from a voice room: enables per-listener volume + local mute
   *  controls for this participant (channelId scopes the master multiplier). */
  voice?: { channelId: string | null };
  /** Own profile only — shows a status picker + a "go to settings" row. */
  onOpenSettings?: () => void;
  /** Hide the "Написать сообщение" button — set when the card is opened from
   *  inside that user's own 1:1 DM (we're already there). */
  hideMessage?: boolean;
}) {
  const { user, guildId, onClose, variant = 'popover', onOpenFull, voice, onOpenSettings, hideMessage } = props;
  const { t } = useTranslation();
  const isDialog = variant === 'dialog';
  // Subscribe so the volume slider / local-mute reflect external changes.
  useVoiceLocalAudio();
  const myId = useAuthStore((s) => s.user?.id ?? null);
  // Live own status from the auth store so the picker's checkmark + dot update
  // the instant we change it (the roster snapshot below can lag).
  const myLiveStatus = useAuthStore((s) => s.user?.status ?? null);
  const upsertDmChannel = useDataStore((s) => s.upsertDmChannel);
  const selectDirectChannel = useDataStore((s) => s.selectDirectChannel);
  const guildOrder = useDataStore((s) => s.guildOrder);
  const guilds = useDataStore((s) => s.guilds);
  // Only servers where I may invite (a closed server offers nothing here).
  const invitableGuildIds = guildOrder.filter((gid) => {
    const g = guilds[gid];
    return !!g && canIn(g, PERMISSIONS.CREATE_INVITES);
  });
  const roster = useGuildRoster(guildId);
  const blocks = useBlockedUsers();
  const friends = useFriends();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);

  const member = roster.findMember(user.id);
  const roleIds = member?.roleIds ?? [];
  const roles: GuildRole[] = roleIds
    .map((id) => roster.rolesById.get(id))
    .filter((r): r is GuildRole => !!r)
    .sort((a, b) => b.position - a.position);
  const isOwner = !!roster.ownerId && roster.ownerId === user.id;
  const theme = useTheme();
  const roleColor = clampRoleColor(resolveRoleColor(roleIds, roster.rolesById), theme);

  // Freshest member data (from the roster) wins over what the trigger passed.
  const ru = member?.user ?? null;
  const status: UserStatus =
    resolveDisplayedStatus((myId === user.id ? myLiveStatus : null) ?? ru?.status ?? user.status, undefined, myId === user.id);
  const statusText = ru?.statusText ?? user.statusText ?? null;
  const bio = ru?.bio ?? user.bio ?? null;
  const avatarUrl = ru?.avatarUrl ?? user.avatarUrl;
  // Under the HeroUI skin, fall back to a glossy orb (HeroUI demo asset)
  // instead of the Aether monogram when the user has no photo.
  const label = member?.nickname || user.displayName || ru?.displayName || user.username;

  const isMe = myId === user.id;
  // The shell's account menu shows the status once it says so (lib/shellPresence.ts).
  const shellHostsPresence = useShellHostsPresence();
  const canDm = !!myId && !isMe;
  const blocked = blocks.isBlocked(user.id);
  const blockPending = blocks.pendingId === user.id;

  // Server ban — separate from the DM block. Available in a guild context to
  // moderators with BAN_MEMBERS who outrank the target (and never the owner).
  const myPerms = useMyGuildPermissions(guildId);
  const canManage =
    !!guildId &&
    !!member &&
    !isMe &&
    canManageMember({
      target: member,
      rolesById: roster.rolesById,
      ownerId: roster.ownerId,
      myTopPosition: myPerms.topRolePosition,
      amOwner: myPerms.isOwner,
    });
  const canBanOnServer = canManage && myPerms.can(PERMISSIONS.BAN_MEMBERS);
  const canKick = canManage && myPerms.can(PERMISSIONS.KICK_MEMBERS);
  const canTimeout = canManage && myPerms.can(PERMISSIONS.TIMEOUT_MEMBERS);
  const timedOut = !!member?.timeoutUntil && new Date(member.timeoutUntil).getTime() > Date.now();
  const serverBans = useGuildBans(guildId, { enabled: canBanOnServer });
  const [confirmBan, setConfirmBan] = useState(false);
  const [confirmKick, setConfirmKick] = useState(false);
  const [timeoutOpen, setTimeoutOpen] = useState(false);
  const [rolesOpen, setRolesOpen] = useState(false);
  const [moderationOpen, setModerationOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  // Role assignment: a moderator with MANAGE_ROLES who outranks the target can
  // toggle any role positioned below their own top role (owner: any role).
  const canManageRoles = canManage && myPerms.can(PERMISSIONS.MANAGE_ROLES);
  const assignableRoles: GuildRole[] = canManageRoles
    ? roster.roles
        .filter((r) => myPerms.isOwner || r.position < myPerms.topRolePosition)
        .sort((a, b) => b.position - a.position)
    : [];
  const memberRoleIds = new Set(member?.roleIds ?? []);

  async function toggleRole(roleId: string) {
    if (busy || !guildId || !member) return;
    const has = member.roleIds.includes(roleId);
    const nextRoleIds = has
      ? member.roleIds.filter((id) => id !== roleId)
      : [...member.roleIds, roleId];
    setBusy(true);
    try {
      await api.updateGuildMember(guildId, user.id, { roleIds: nextRoleIds });
      void qc.invalidateQueries({ queryKey: guildRosterKey(guildId) });
      toast.success(has ? t('prof.card.roleRemoved') : t('prof.card.roleGranted'));
    } catch (err) {
      toast.error(...errToToast(err, t('prof.card.roleChangeFailed')));
    } finally {
      setBusy(false);
    }
  }

  async function moderate(action: () => Promise<unknown>, ok: string) {
    if (busy || !guildId) return;
    setBusy(true);
    try {
      await action();
      void qc.invalidateQueries({ queryKey: guildRosterKey(guildId) });
      toast.success(ok);
      onClose();
    } catch (err) {
      toast.error(...errToToast(err, t('prof.card.actionFailed')));
    } finally {
      setBusy(false);
    }
  }

  const TIMEOUTS: { label: string; minutes: number | null }[] = [
    { label: t('prof.card.timeout5m'), minutes: 5 },
    { label: t('prof.card.timeout1h'), minutes: 60 },
    { label: t('prof.card.timeout1d'), minutes: 60 * 24 },
    { label: t('prof.card.timeout1w'), minutes: 60 * 24 * 7 },
  ];
  const [inviteOpen, setInviteOpen] = useState(false);

  async function inviteToServer(gid: string) {
    if (busy) return;
    setBusy(true);
    try {
      // Personal invite to one user only, so a forwarded DM does not become
      // an open server entrance.
      const invite = await api.createInvite(gid, { maxUses: 1 });
      const url = buildPublicInviteUrl(invite.code);
      const dm = await api.openDirectMessage(user.id);
      upsertDmChannel(dm);
      await api.sendMessage(dm.id, { content: t('prof.card.inviteMessage', { url }) });
      toast.success(t('prof.card.inviteSent'));
      onClose();
    } catch (err) {
      toast.error(...errToToast(err, t('prof.card.inviteFailed')));
    } finally {
      setBusy(false);
    }
  }

  async function openDm() {
    if (!canDm || busy) return;
    setBusy(true);
    try {
      const channel = await api.openDirectMessage(user.id);
      upsertDmChannel(channel);
      selectDirectChannel(channel.id);
      onClose();
    } catch (err) {
      toast.error(...errToToast(err, t('prof.card.openDmFailed')));
    } finally {
      setBusy(false);
    }
  }

  function copy(text: string, what: string) {
    if (!navigator.clipboard) {
      toast.error(t('prof.card.copyUnavailable'));
      return;
    }
    void navigator.clipboard.writeText(text).then(
      () => toast.success(t('prof.card.copied', { what })),
      () => toast.error(t('prof.card.copyFailed')),
    );
  }

  // Which status row has its duration list expanded (Discord "set for…").
  const [statusExpanded, setStatusExpanded] = useState<UserStatus | null>(null);

  async function applyStatus(next: UserStatus, durationMs: number | null) {
    if (busy) return;
    setBusy(true);
    try {
      await setStatusFor(next, durationMs);
      setStatusExpanded(null);
    } catch (err) {
      toast.error(...errToToast(err, t('prof.card.statusUpdateFailed')));
    } finally {
      setBusy(false);
    }
  }

  const heroColor = roleColor ?? 'rgb(var(--bk-accent))';
  const heroGradient = `linear-gradient(135deg, ${heroColor} 0%, transparent 95%), radial-gradient(circle at 30% 20%, rgb(var(--bk-accent) / 0.35), transparent 60%)`;
  // "Участник с" — humanised join date, only meaningful in a guild context.
  const joinedLabel = member?.joinedAt
    ? new Date(member.joinedAt).toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  return (
    <div className={'w-full overflow-hidden ' + (isDialog ? 'flex flex-col md:flex-row bg-surface rounded-2xl border border-separator' : '')}>
      <div className={isDialog ? 'flex w-full min-w-0 shrink-0 flex-col md:w-[340px]' : 'contents'}>
      {isDialog && (
        <div className="studio-profile-hero h-24" style={{ backgroundImage: heroGradient }} />
      )}

      <div className={isDialog ? '-mt-12 flex flex-col items-center px-5' : 'flex items-center gap-3 px-4 py-4'}>
        <div className="relative">
          <IdentityAvatar id={user.id} name={label} src={avatarUrl} size="lg" className="studio-profile-avatar shrink-0" />
          <span
            title={presenceLabel(status)}
            className={
              'absolute bottom-1 right-1 h-4 w-4 rounded-full ring-[3px] ' +
              presenceDotClass(status)
            }
            style={{ '--tw-ring-color': 'rgb(var(--surface-card))' } as React.CSSProperties}
          />
        </div>
        <div className={isDialog ? 'mt-2 flex flex-col items-center gap-0.5 text-center' : 'min-w-0 flex flex-col gap-0.5'}>
          <span
            className="studio-profile-name truncate text-[16px] font-semibold tracking-[-0.02em] text-ink"
            style={roleColor ? { color: roleColor } : undefined}
          >
            {label}
          </span>
          <span className="text-[11px] text-ink-muted">@{user.username}</span>
          <TeamTag
            tag={ru?.teamTag ?? user.teamTag}
            name={ru?.teamName ?? user.teamName}
            className={isDialog ? 'mt-0.5' : ''}
          />
          {isDialog && (
            <span className="mt-1 flex items-center gap-1.5 text-[11px] text-ink-muted">
              <span className={'h-2 w-2 rounded-full ' + presenceDotClass(status)} />
              {presenceLabel(status)}
            </span>
          )}
        </div>
        {isOwner && (
          <span
            className={isDialog ? 'mt-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium' : 'ml-auto inline-flex shrink-0 items-center gap-1 text-[11px]'}
            style={isDialog ? { backgroundColor: 'rgb(var(--bk-warning) / 0.18)', color: 'rgb(var(--bk-warning))' } : { color: 'rgb(var(--bk-warning))' }}
          >
            <CrownIcon className="h-3 w-3" />
            {t('prof.card.serverOwner')}
          </span>
        )}
      </div>

      {/* Profile sections — status text, bio, roles, join date (full dialog). */}
      <UserCardProfileSections
        isDialog={isDialog}
        joinedLabel={joinedLabel}
        statusText={statusText}
        bio={bio}
        roles={roles}
      />

      {/* Actions */}
      <div className="app-profile-actions flex flex-col gap-1 border-t border-separator p-3" data-more-open={moreOpen || undefined}>
        {isMe && !shellHostsPresence && (
          <>
            <div className="px-1 pb-0.5 text-[10px] font-medium uppercase tracking-wide text-ink-muted">
              {t('prof.card.statusLabel')}
            </div>
            {STATUS_OPTIONS.map((opt) => (
              <div key={opt.value}>
                <ActionRow
                  icon={
                    <span className={'h-2.5 w-2.5 rounded-full ' + presenceDotClass(opt.value)} />
                  }
                  label={t(opt.labelKey)}
                  disabled={busy}
                  badge={status === opt.value ? '✓' : undefined}
                  onClick={() => {
                    // "В сети" applies at once (and clears any timed revert);
                    // the others expand the "set for…" duration list.
                    if (opt.value === 'ONLINE') void applyStatus('ONLINE', null);
                    else setStatusExpanded((cur) => (cur === opt.value ? null : opt.value));
                  }}
                />
                {statusExpanded === opt.value && (
                  <div className="flex flex-col gap-0.5 pl-7">
                    {STATUS_DURATIONS.map((d) => (
                      <Button
                        key={d.labelKey}
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        onClick={() => void applyStatus(opt.value, d.ms)}
                        className="w-full justify-start"
                      >
                        <span className="min-w-0 flex-1 truncate text-left">{t(d.labelKey)}</span>
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {onOpenSettings && (
              <ActionRow
                icon={<SettingsIcon className="h-4 w-4" />}
                label={t('prof.card.goToSettings')}
                onClick={() => {
                  onClose();
                  onOpenSettings();
                }}
              />
            )}
            <div className="h-2" aria-hidden />
          </>
        )}
        {canDm && !hideMessage && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => void openDm()}
            disabled={busy}
            leftIcon={<ChatBubbleIcon className="h-4 w-4" />}
            className="w-full"
          >
            {t('prof.card.sendMessage')}
          </Button>
        )}
        {voice && !isMe && (
          <div className="flex flex-col gap-1.5 rounded-xl px-2.5 py-2">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-xs text-ink">
                <VolumeIcon className="h-4 w-4 text-ink-muted" />
                {t('prof.card.volume')}
              </span>
              <span className="text-[11px] tabular-nums text-ink-muted">
                {getUserVolume(user.id)}%
              </span>
            </div>
            <RangeSlider
              ariaLabel={t('prof.card.peerVolume')}
              min={0}
              max={200}
              step={5}
              value={getUserVolume(user.id)}
              onChange={(v) => setUserVolume(user.id, v)}
            />
          </div>
        )}
        {voice && !isMe && (
          <ActionRow
            icon={
              isLocallyMuted(user.id) ? (
                <VolumeIcon className="h-4 w-4" />
              ) : (
                <VolumeOffIcon className="h-4 w-4" />
              )
            }
            label={isLocallyMuted(user.id) ? t('prof.card.unmute') : t('prof.card.muteLocal')}
            onClick={() => toggleLocalMute(user.id)}
          />
        )}

        {canDm && (() => {
          const fstate = friends.stateOf(user.id);
          const fpending = friends.pendingId === user.id;
          if (fstate === 'friends') {
            return (
              <ActionRow
                icon={<UsersIcon className="h-4 w-4" />}
                label={t('prof.card.removeFriend')}
                danger
                disabled={fpending}
                onClick={() => friends.remove(user.id)}
              />
            );
          }
          if (fstate === 'incoming') {
            return (
              <>
                <ActionRow
                  icon={<UsersIcon className="h-4 w-4" />}
                  label={t('prof.card.acceptFriend')}
                  disabled={fpending}
                  onClick={() => friends.accept(user.id)}
                />
                <ActionRow
                  icon={<CloseIcon className="h-4 w-4" />}
                  label={t('prof.card.declineFriend')}
                  danger
                  disabled={fpending}
                  onClick={() => friends.remove(user.id)}
                />
              </>
            );
          }
          if (fstate === 'outgoing') {
            return (
              <ActionRow
                icon={<UsersIcon className="h-4 w-4" />}
                label={t('prof.card.cancelFriend')}
                disabled={fpending}
                onClick={() => friends.remove(user.id)}
              />
            );
          }
          return (
            <ActionRow
              icon={<UsersIcon className="h-4 w-4" />}
              label={t('prof.card.addFriend')}
              disabled={fpending}
              onClick={() => friends.sendRequest(user.id)}
            />
          );
        })()}

        {!isDialog && onOpenFull && (
          <button
            type="button"
            className="app-profile-full-link"
            onClick={() => {
              // Your own profile lives on the platform: inside its Chat frame,
              // ask the shell to show it instead of Bunker's copy.
              if (isMe && isPlatformEmbedded()) {
                openPlatformView('profile');
                onClose();
              } else {
                onOpenFull();
              }
            }}
          >
            <span>{t('prof.card.fullProfile')}</span><span aria-hidden>→</span>
          </button>
        )}
        <MutualGuilds userId={user.id} isMe={isMe} />

        {(canTimeout || canManageRoles || canKick || canBanOnServer) && (
          <CardDisclosure
            label={t('prof.card.manageMember')}
            open={moderationOpen}
            onClick={() => setModerationOpen((value) => !value)}
          />
        )}
        {moderationOpen && (
          <div className="app-profile-action-group">
        {canTimeout && (
          <>
            <ActionRow
              icon={<AlarmClockIcon className="h-4 w-4" />}
              label={timedOut ? t('prof.card.removeTimeout') : t('prof.card.timeout')}
              disabled={busy}
              onClick={() => {
                if (timedOut) {
                  void moderate(
                    () => api.timeoutMember(guildId as string, user.id, { timeoutUntil: null }),
                    t('prof.card.timeoutRemoved'),
                  );
                } else {
                  setTimeoutOpen((o) => !o);
                }
              }}
            />
            {timeoutOpen && !timedOut && (
              <div className="flex flex-col gap-0.5 pl-2">
                {TIMEOUTS.map((to) => (
                  <Button
                    key={to.label}
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      void moderate(
                        () =>
                          api.timeoutMember(guildId as string, user.id, {
                            timeoutUntil: new Date(
                              Date.now() + (to.minutes ?? 0) * 60_000,
                            ).toISOString(),
                          }),
                        t('prof.card.timeoutApplied', { duration: to.label }),
                      )
                    }
                    className="w-full justify-start"
                  >
                    <span className="min-w-0 flex-1 truncate text-left">{to.label}</span>
                  </Button>
                ))}
              </div>
            )}
          </>
        )}

        {/* A MANAGE_ROLES holder whose top role sits below every assignable
            role used to get NOTHING here — indistinguishable from having no
            permission at all («не могу выдавать роли»). Show the row with an
            explanation instead of silently hiding it. */}
        {canManageRoles && assignableRoles.length === 0 && (
          <p className="px-3 py-1.5 text-[11px] leading-snug text-ink-muted">
            {t('prof.card.rolesHierarchyHint')}
          </p>
        )}
        {canManageRoles && assignableRoles.length > 0 && (
          <>
            <ActionRow
              icon={<ShieldIcon className="h-4 w-4" />}
              label={t('prof.card.roles')}
              disabled={busy}
              onClick={() => setRolesOpen((o) => !o)}
            />
            {rolesOpen && (
              <div className="flex max-h-44 flex-col gap-0.5 overflow-y-auto pl-2">
                {assignableRoles.map((r) => {
                  const has = memberRoleIds.has(r.id);
                  const color =
                    clampRoleColor(`#${r.color.toString(16).padStart(6, '0')}`, theme) ?? undefined;
                  return (
                    <Button
                      key={r.id}
                      variant="ghost"
                      size="sm"
                      disabled={busy}
                      onClick={() => void toggleRole(r.id)}
                      leftIcon={
                        <span
                          className="h-2 w-2 shrink-0 rounded-full"
                          style={{ backgroundColor: color ?? 'currentColor' }}
                        />
                      }
                      rightIcon={has ? <span className="shrink-0 text-primary">✓</span> : undefined}
                      className="w-full justify-start gap-2"
                    >
                      <span className="min-w-0 flex-1 truncate text-left">{r.name}</span>
                    </Button>
                  );
                })}
              </div>
            )}
          </>
        )}

        {canKick && (
          <ActionRow
            icon={<LogOutIcon className="h-4 w-4" />}
            label={confirmKick ? t('prof.card.kickConfirm') : t('prof.card.kick')}
            danger
            disabled={busy}
            onClick={() => {
              if (!confirmKick) {
                setConfirmKick(true);
                return;
              }
              void moderate(() => api.kickMember(guildId as string, user.id), t('prof.card.kicked'));
            }}
          />
        )}

        {canBanOnServer && (
          <ActionRow
            icon={<ShieldBanIcon className="h-4 w-4" />}
            label={confirmBan ? t('prof.card.banConfirm') : t('prof.card.ban')}
            danger
            disabled={serverBans.pendingId === user.id}
            onClick={() => {
              if (!confirmBan) {
                setConfirmBan(true);
                return;
              }
              serverBans.ban(user.id);
              onClose();
            }}
          />
        )}
          </div>
        )}

        <CardDisclosure
          label={t(moreOpen && !isDialog ? 'prof.card.backToCard' : 'prof.card.moreActions')}
          open={moreOpen}
          onClick={() => setMoreOpen((value) => !value)}
        />
        {moreOpen && (
          <div className="app-profile-action-group app-profile-more-group">
            {canDm && invitableGuildIds.length > 0 && (
              <div className="app-profile-extra-section">
                <ActionRow
                  icon={<PlusIcon className="h-4 w-4" />}
                  label={t('prof.card.inviteToServer')}
                  disabled={busy}
                  onClick={() => setInviteOpen((o) => !o)}
                />
                {inviteOpen && (
                  <div className="flex flex-col gap-0.5 pl-2">
                    {invitableGuildIds.map((gid) => {
                      const g = guilds[gid];
                      if (!g) return null;
                      return (
                        <Button key={gid} variant="ghost" size="sm" disabled={busy}
                          onClick={() => void inviteToServer(gid)} className="w-full justify-start">
                          <span className="min-w-0 flex-1 truncate text-left">{g.name}</span>
                        </Button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            <div className="app-profile-extra-section">
              <p className="app-profile-extra-heading">{t('prof.card.safetyActions')}</p>
            {canDm && (
              <ActionRow
                icon={blocked ? <ShieldIcon className="h-4 w-4" /> : <ShieldBanIcon className="h-4 w-4" />}
                label={blocked ? t('prof.card.unblockDm') : t('prof.card.blockDm')}
                danger={!blocked}
                disabled={blockPending}
                onClick={() => blocks.toggle(user.id)}
              />
            )}
            {!isMe && (
              <ActionRow
                icon={<FlagIcon className="h-4 w-4" />}
                label={t('report.reportUser')}
                onClick={() => {
                  onClose();
                  openReportModal({ kind: 'user', targetId: user.id, targetName: label });
                }}
              />
            )}
            </div>
            <div className="app-profile-extra-section">
              <p className="app-profile-extra-heading">{t('prof.card.copyActions')}</p>
            <ActionRow
              icon={<CopyIcon className="h-4 w-4" />}
              label={t('prof.card.copyNick')}
              onClick={() => copy(`@${user.username}`, t('prof.card.nick'))}
            />
            <ActionRow
              icon={<CopyIcon className="h-4 w-4" />}
              label={t('prof.card.copyId')}
              onClick={() => copy(user.id, t('prof.card.id'))}
            />
            </div>
          </div>
        )}
      </div>
      </div>
      {isDialog && <ProfileSidePane userId={user.id} name={label} bio={bio} />}
    </div>
  );
}

function CardDisclosure(props: { label: string; open: boolean; onClick: () => void }) {
  return (
    <Button
      variant="ghost"
      size="sm"
      aria-expanded={props.open}
      onClick={props.onClick}
      rightIcon={<ChevronDownIcon className={'h-4 w-4 transition-transform ' + (props.open ? 'rotate-180' : '')} />}
      className="app-profile-disclosure w-full justify-between"
    >
      {props.label}
    </Button>
  );
}

/** "Общие серверы" — servers the viewer and this user both belong to. Hidden
 *  for self / no overlap; soft-fails to nothing if the endpoint errors. */
function MutualGuilds({ userId, isMe }: { userId: string; isMe: boolean }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const { data } = useQuery({
    queryKey: ['mutual-guilds', userId],
    queryFn: () => api.getMutualGuilds(userId),
    enabled: !isMe,
    staleTime: 60_000,
  });
  const list = data?.guilds ?? [];
  if (isMe || list.length === 0) return null;
  return (
    <div className="app-profile-mutual mt-1">
      <div className="flex items-center justify-between px-1 pb-1.5 text-[11px] font-medium text-ink-muted">
        <span>{t('prof.card.mutualServers')} · {list.length}</span>
        <button type="button" className="app-profile-mutual-toggle" onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}>
          {expanded ? t('prof.card.showLess') : t('prof.card.showAll')}
        </button>
      </div>
      {expanded && <div className="flex flex-wrap gap-1.5 px-1">
        {list.map((g) => (
          <span
            key={g.id}
            className="app-profile-server inline-flex max-w-full items-center gap-1.5 rounded-lg px-1.5 py-1 text-[11px] text-ink-dim"
            title={g.name}
          >
            {/* Squircle: this is a server, and a server is a squircle app-wide
                — circles are for people. */}
            <IdentityAvatar id={g.id} name={g.name} src={g.iconUrl} size="xs" className="studio-guild-avatar" />
            <span className="max-w-[140px] truncate">{g.name}</span>
          </span>
        ))}
      </div>}
    </div>
  );
}
